<script lang="ts">
	import * as Sentry from '@sentry/sveltekit';
	import ScrollReveal from '$lib/components/ScrollReveal.svelte';
	import { t, type Language } from '$lib/i18n';
	import { isValidEmail, validateField } from '$lib/utils/validation';
	import { captureEvent, identifyUser } from '$lib/analytics/posthog';
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';

	interface Props {
		lang: Language;
	}

	let { lang }: Props = $props();
	let tr = $derived(t(lang));

	const STORAGE_KEY = 'contact-form-draft';

	// Form state
	let form = $state({
		name: '',
		email: '',
		company: '',
		challenge: '',
		message: ''
	});

	// Restore from sessionStorage
	onMount(() => {
		if (browser) {
			try {
				const saved = sessionStorage.getItem(STORAGE_KEY);
				if (saved) {
					const parsed = JSON.parse(saved);
					form = { ...form, ...parsed };
				}
			} catch {
				/* ignore */
			}
		}
	});

	// Debounced save to sessionStorage
	let saveTimeout: ReturnType<typeof setTimeout>;
	$effect(() => {
		if (!browser) return;
		const snapshot = JSON.stringify(form);
		clearTimeout(saveTimeout);
		saveTimeout = setTimeout(() => {
			sessionStorage.setItem(STORAGE_KEY, snapshot);
		}, 300);
	});

	let errors = $state<Partial<Record<'name' | 'email' | 'message', string>>>({});
	let isSubmitting = $state(false);
	let submitStatus = $state<'idle' | 'success' | 'error' | 'rate_limited'>('idle');
	let submitError = $state('');

	function validateForm(): boolean {
		const newErrors: Partial<Record<'name' | 'email' | 'message', string>> = {};

		const nameResult = validateField(form.name, [
			{ type: 'required', message: tr('contact_form_error_name') }
		]);
		if (!nameResult.valid) newErrors.name = nameResult.error;

		if (!form.email.trim()) {
			newErrors.email = tr('contact_form_error_email');
		} else if (!isValidEmail(form.email)) {
			newErrors.email = tr('contact_form_error_email_invalid');
		}

		const msgResult = validateField(form.message, [
			{ type: 'required', message: tr('contact_form_error_message') }
		]);
		if (!msgResult.valid) newErrors.message = msgResult.error;

		errors = newErrors;
		return Object.keys(newErrors).length === 0;
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();

		if (!validateForm()) return;

		isSubmitting = true;
		submitStatus = 'idle';
		submitError = '';

		captureEvent('contact_form_submitted', {
			challenge: form.challenge || 'none',
			has_company: !!form.company.trim(),
			lang
		});

		try {
			const response = await fetch('/api/contact', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ...form, lang })
			});

			if (response.status === 429) {
				const retryAfter = response.headers.get('Retry-After');
				const seconds = retryAfter ? parseInt(retryAfter, 10) : 60;
				const minutes = Math.ceil(seconds / 60);
				submitStatus = 'rate_limited';
				submitError =
					lang === 'no'
						? `For mange forespørsler. Prøv igjen om ${minutes} minutt${minutes > 1 ? 'er' : ''}.`
						: `Too many submissions. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`;
				captureEvent('contact_form_error', { type: 'rate_limited', lang });
				return;
			}

			await response.json();

			if (!response.ok) {
				submitStatus = 'error';
				captureEvent('contact_form_error', { type: 'server_error', lang });
				return;
			}

			submitStatus = 'success';
			captureEvent('contact_form_success', { challenge: form.challenge || 'none', lang });

			// Identify user in Sentry and PostHog for better error context
			const userEmail = form.email;
			const userName = form.name;
			Sentry.setUser({ email: userEmail, username: userName });
			identifyUser(userEmail, { name: userName, company: form.company || undefined });

			form = { name: '', email: '', company: '', challenge: '', message: '' };
			if (browser) sessionStorage.removeItem(STORAGE_KEY);
		} catch {
			submitStatus = 'error';
			captureEvent('contact_form_error', { type: 'network_error', lang });
		} finally {
			isSubmitting = false;
		}
	}

	function clearError(field: 'name' | 'email' | 'message') {
		if (errors[field]) {
			const { [field]: _, ...rest } = errors;
			errors = rest;
		}
	}
</script>

<section id="contact" class="py-16 md:py-32 relative">
	<div class="absolute inset-0">
		<div
			class="absolute bottom-0 left-0 w-[600px] h-[600px] bg-ameta-green/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"
		></div>
	</div>

	<div class="relative max-w-7xl mx-auto px-6">
		<div class="grid lg:grid-cols-2 gap-16">
			<ScrollReveal>
				<div>
					<p class="text-ameta-green font-medium mb-4 tracking-wide">{tr('contact_label')}</p>
					<h2 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
						{tr('contact_title')}
					</h2>
					<p class="text-xl text-slate-400 leading-relaxed mb-10">
						{tr('contact_subtitle')}
					</p>

					<div class="space-y-6">
						<a href="mailto:hello@ameta.no" class="flex items-center gap-4 group">
							<div
								class="w-14 h-14 rounded-xl bg-ameta-green/10 flex items-center justify-center text-ameta-green group-hover:bg-ameta-green/20 transition-colors"
							>
								<svg
									aria-hidden="true"
									class="w-6 h-6"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
									/>
								</svg>
							</div>
							<div>
								<p class="text-sm text-slate-500">{tr('contact_email_label')}</p>
								<p
									class="text-lg font-medium text-slate-100 group-hover:text-ameta-green transition-colors"
								>
									hello@ameta.no
								</p>
							</div>
						</a>

						<div class="flex items-center gap-4">
							<div
								class="w-14 h-14 rounded-xl bg-ameta-green/10 flex items-center justify-center text-ameta-green"
							>
								<svg
									aria-hidden="true"
									class="w-6 h-6"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
									/>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
									/>
								</svg>
							</div>
							<div>
								<p class="text-sm text-slate-500">{tr('contact_office_label')}</p>
								<p class="text-lg font-medium text-slate-100">Kjøita 40, 4630 Kristiansand</p>
							</div>
						</div>
					</div>
				</div>
			</ScrollReveal>

			<!-- Contact form -->
			<ScrollReveal delay={150}>
				<div class="glass-strong rounded-2xl p-8">
					<form class="space-y-6" onsubmit={handleSubmit}>
						<!-- Honeypot field — hidden from real users, filled by bots -->
						<div class="absolute opacity-0 -z-10" aria-hidden="true">
							<label for="challenge">Leave this empty</label>
							<input
								type="text"
								id="challenge"
								name="challenge"
								bind:value={form.challenge}
								autocomplete="off"
								tabindex="-1"
							/>
						</div>
						{#if submitStatus === 'success'}
							<div
								class="p-4 rounded-xl bg-[#4ACB85]/20 border border-[#4ACB85]/30 text-[#4ACB85]"
								role="status"
								aria-live="polite"
							>
								<p class="font-medium">{tr('contact_form_success_title')}</p>
								<p class="text-sm opacity-80">{tr('contact_form_success_desc')}</p>
							</div>
						{/if}

						{#if submitStatus === 'error'}
							<div
								class="p-4 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400"
								role="alert"
								aria-live="assertive"
							>
								<p class="font-medium">{tr('contact_form_error_title')}</p>
								<p class="text-sm opacity-80">{tr('contact_form_error_desc')}</p>
							</div>
						{/if}

						{#if submitStatus === 'rate_limited'}
							<div
								class="p-4 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-400"
								role="alert"
								aria-live="assertive"
							>
								<p class="font-medium">{submitError}</p>
							</div>
						{/if}

						<div class="grid sm:grid-cols-2 gap-5">
							<div>
								<label for="name" class="block text-sm font-medium text-slate-300 mb-2"
									>{tr('contact_form_name')}</label
								>
								<input
									type="text"
									id="name"
									name="name"
									bind:value={form.name}
									oninput={() => clearError('name')}
									aria-describedby={errors.name ? 'name-error' : undefined}
									aria-invalid={errors.name ? 'true' : undefined}
									class="w-full px-4 py-3 bg-slate-800/50 border rounded-xl text-slate-100 placeholder-slate-500 focus:border-ameta-green focus:ring-1 focus:ring-ameta-green transition-all {errors.name
										? 'border-red-500'
										: 'border-slate-700'}"
									placeholder={tr('contact_form_name_placeholder')}
								/>
								{#if errors.name}
									<p id="name-error" role="alert" class="mt-1 text-sm text-red-400">
										{errors.name}
									</p>
								{/if}
							</div>
							<div>
								<label for="email" class="block text-sm font-medium text-slate-300 mb-2"
									>{tr('contact_form_email')}</label
								>
								<input
									type="email"
									id="email"
									name="email"
									bind:value={form.email}
									oninput={() => clearError('email')}
									aria-describedby={errors.email ? 'email-error' : undefined}
									aria-invalid={errors.email ? 'true' : undefined}
									class="w-full px-4 py-3 bg-slate-800/50 border rounded-xl text-slate-100 placeholder-slate-500 focus:border-ameta-green focus:ring-1 focus:ring-ameta-green transition-all {errors.email
										? 'border-red-500'
										: 'border-slate-700'}"
									placeholder="you@company.com"
								/>
								{#if errors.email}
									<p id="email-error" role="alert" class="mt-1 text-sm text-red-400">
										{errors.email}
									</p>
								{/if}
							</div>
						</div>

						<div>
							<label for="company" class="block text-sm font-medium text-slate-300 mb-2"
								>{tr('contact_form_company')}</label
							>
							<input
								type="text"
								id="company"
								name="company"
								bind:value={form.company}
								class="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-slate-100 placeholder-slate-500 focus:border-ameta-green focus:ring-1 focus:ring-ameta-green transition-all"
								placeholder={tr('contact_form_company_placeholder')}
							/>
						</div>

						<div>
							<label for="message" class="block text-sm font-medium text-slate-300 mb-2"
								>{tr('contact_form_message')}</label
							>
							<textarea
								id="message"
								name="message"
								rows="4"
								bind:value={form.message}
								oninput={() => clearError('message')}
								aria-describedby={errors.message ? 'message-error' : undefined}
								aria-invalid={errors.message ? 'true' : undefined}
								class="w-full px-4 py-3 bg-slate-800/50 border rounded-xl text-slate-100 placeholder-slate-500 focus:border-ameta-green focus:ring-1 focus:ring-ameta-green transition-all resize-none {errors.message
									? 'border-red-500'
									: 'border-slate-700'}"
								placeholder={tr('contact_form_message_placeholder')}
							></textarea>
							{#if errors.message}
								<p id="message-error" role="alert" class="mt-1 text-sm text-red-400">
									{errors.message}
								</p>
							{/if}
						</div>

						<button
							type="submit"
							class="w-full btn-primary text-lg disabled:opacity-50 disabled:cursor-not-allowed"
							disabled={isSubmitting}
						>
							{#if isSubmitting}
								<span class="flex items-center justify-center gap-2">
									<svg
										aria-hidden="true"
										class="animate-spin w-5 h-5"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											class="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											stroke-width="4"
										></circle>
										<path
											class="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									{tr('contact_form_submitting')}
								</span>
							{:else}
								<span>{tr('contact_form_submit')}</span>
							{/if}
						</button>

						<p class="text-sm text-slate-500 text-center">
							{tr('contact_form_expect')}
						</p>
					</form>
				</div>
			</ScrollReveal>
		</div>
	</div>
</section>
